import { Margin, usePDF } from "react-to-pdf";
import {
  Box,
  Button,
  CircularProgress,
  LinearProgress,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import PrintIcon from "@mui/icons-material/Print";
import FileDownloadIcon from "@mui/icons-material/FileDownload";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { Dayjs } from "dayjs";
import { useEffect, useState } from "react";
import {
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  LabelList,
  Legend,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import { axiosInstance } from "../../../../utils/networking";

const PRIMARY = "#9D2362";
const CARD_BORDER = "1px solid #ececf0";

// Rotating palette for the categorical segments.
const CATEGORY_COLORS = [
  PRIMARY,
  "#3F51B5",
  "#00897b",
  "#fdb814",
  "#c2185b",
  "#5c6bc0",
  "#26a69a",
  "#ef6c00",
  "#7e57c2",
  "#43a047",
  "#8d6e63",
  "#9e9e9e",
];

// ---- Small presentational helpers -------------------------------------------

const SectionCard = ({ children, sx }: { children: any; sx?: any }) => (
  <Paper
    elevation={0}
    sx={{
      p: 3,
      borderRadius: "16px",
      border: CARD_BORDER,
      backgroundColor: "white",
      ...sx,
    }}
  >
    {children}
  </Paper>
);

const SectionTitle = ({
  children,
  subtitle,
}: {
  children: any;
  subtitle?: string;
}) => (
  <Box sx={{ mb: 2 }}>
    <Typography
      variant="h6"
      sx={{
        fontWeight: 700,
        color: "#2c2c34",
        borderLeft: `4px solid ${PRIMARY}`,
        pl: 1.5,
        lineHeight: 1.25,
      }}
    >
      {children}
    </Typography>
    {subtitle ? (
      <Typography variant="body2" sx={{ color: "#8a8a93", pl: 1.5, mt: 0.5 }}>
        {subtitle}
      </Typography>
    ) : null}
  </Box>
);

// Divider between the three merged report areas.
const GroupHeading = ({
  children,
  subtitle,
}: {
  children: any;
  subtitle?: string;
}) => (
  <Box sx={{ mb: 2, mt: 1 }}>
    <Typography
      variant="h6"
      sx={{
        fontWeight: 800,
        color: PRIMARY,
        textTransform: "uppercase",
        letterSpacing: "1px",
        fontSize: "0.95rem",
      }}
    >
      {children}
    </Typography>
    {subtitle ? (
      <Typography variant="body2" sx={{ color: "#8a8a93", mt: 0.25 }}>
        {subtitle}
      </Typography>
    ) : null}
    <Box
      sx={{
        mt: 1,
        height: "2px",
        backgroundColor: "#ececf0",
        borderRadius: 1,
      }}
    />
  </Box>
);

const StatCard = ({
  label,
  value,
  accent,
}: {
  label: string;
  value: any;
  accent: string;
}) => (
  <Paper
    elevation={0}
    sx={{
      flex: "1 1 180px",
      p: 2.5,
      borderRadius: "16px",
      border: CARD_BORDER,
      borderTop: `3px solid ${accent}`,
      backgroundColor: "white",
      textAlign: "center",
    }}
  >
    <Typography
      sx={{
        fontSize: "2rem",
        fontWeight: 800,
        color: "#2c2c34",
        lineHeight: 1.1,
      }}
    >
      {value}
    </Typography>
    <Typography
      variant="caption"
      sx={{
        color: "#8a8a93",
        textTransform: "uppercase",
        letterSpacing: "0.5px",
        fontWeight: 600,
      }}
    >
      {label}
    </Typography>
  </Paper>
);

const ChartCard = ({
  title,
  subtitle,
  height,
  children,
}: {
  title: string;
  subtitle?: string;
  height: number | string;
  children: any;
}) => (
  <SectionCard sx={{ flex: "1 1 380px", minWidth: 0 }}>
    <SectionTitle subtitle={subtitle}>{title}</SectionTitle>
    <Box sx={{ height }}>
      <ResponsiveContainer width="100%" height="100%">
        {children}
      </ResponsiveContainer>
    </Box>
  </SectionCard>
);

const GeneralClientReport = () => {
  const [report, setReport] = useState<any>(null);
  const [demographics, setDemographics] = useState<any>(null);
  const [operations, setOperations] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [startDate, setStartDate] = useState<Dayjs | null>(null);
  const [endDate, setEndDate] = useState<Dayjs | null>(null);

  const fetchReport = (start: Dayjs | null, end: Dayjs | null) => {
    setLoading(true);
    const params: string[] = [];
    if (start) params.push(`startDate=${start.format("YYYY-MM-DD")}`);
    if (end) params.push(`endDate=${end.format("YYYY-MM-DD")}`);
    const query = params.length > 0 ? `?` + params.join("&") : ``;

    // allSettled so one failing section does not blank out the whole report.
    Promise.allSettled([
      axiosInstance.get(`/reports/general_client${query}`),
      axiosInstance.get(`/reports/client_demographics${query}`),
      axiosInstance.get(`/reports/engagement_operations${query}`),
    ])
      .then((results: any[]) => {
        const dataOf = (result: any) =>
          result.status === "fulfilled" ? result.value.data : null;
        setReport(dataOf(results[0]));
        setDemographics(dataOf(results[1]));
        setOperations(dataOf(results[2]));
      })
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    fetchReport(null, null);
  }, []);

  const { toPDF, targetRef } = usePDF({
    filename: "General_Client_Report.pdf",
    page: { margin: Margin.SMALL, format: "letter" },
    overrides: {
      canvas: {
        removeContainer: false,
      },
    },
  });

  const handleCsvDownload = () => {
    if (!report && !demographics && !operations) return;
    const categoryRows: any[] = report?.categories || [];
    const ownershipRows: any[] = demographics?.ownership || [];
    const industryRows: any[] = demographics?.byIndustry || [];
    const cityRows: any[] = demographics?.byCity || [];
    const countyRows: any[] = demographics?.byCounty || [];
    const employeeRows: any[] = demographics?.byEmployeeBand || [];
    const revenueRows: any[] = demographics?.byRevenueBand || [];
    const advisorRows: any[] = operations?.byAdvisor || [];
    const monthRows: any[] = operations?.casesByMonth || [];
    const activityRows: any[] = operations?.activityMix || [];

    const csvContent: any[] = [];
    csvContent.push(["Section", "Dimension", "Value", "Detail"]);
    for (const row of categoryRows) {
      csvContent.push([
        "Assistance",
        "Category",
        row.category,
        `${row.count} (${Math.round((row.percentage || 0) * 100)}%)`,
      ]);
    }
    for (const row of ownershipRows) {
      csvContent.push([
        "Demographics",
        "Ownership",
        row.label,
        `${row.count} (${Math.round((row.pct || 0) * 100)}%)`,
      ]);
    }
    for (const row of industryRows) {
      csvContent.push(["Demographics", "Industry", row.label, row.count]);
    }
    for (const row of cityRows) {
      csvContent.push(["Demographics", "City", row.label, row.count]);
    }
    for (const row of countyRows) {
      csvContent.push(["Demographics", "County", row.label, row.count]);
    }
    for (const row of employeeRows) {
      csvContent.push(["Demographics", "Employees", row.label, row.count]);
    }
    for (const row of revenueRows) {
      csvContent.push(["Demographics", "Revenue", row.label, row.count]);
    }
    for (const row of monthRows) {
      csvContent.push([
        "Operations",
        "Cases By Month",
        row.month,
        `created ${row.created} / closed ${row.closed}`,
      ]);
    }
    for (const row of advisorRows) {
      csvContent.push([
        "Operations",
        "Advisor",
        row.advisor_name,
        `open ${row.open} / completed ${row.completed} / total ${row.total}`,
      ]);
    }
    for (const row of activityRows) {
      csvContent.push(["Operations", "Activity", row.label, row.count]);
    }

    let csvString = ``;
    for (const row of csvContent) {
      let rowString = `\n`;
      for (const item of row) {
        rowString = rowString + `"${item}",`;
      }
      csvString = csvString + rowString;
    }

    const blob = new Blob([csvString], { type: "text/csv" });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `general_client_report.csv`;
    document.body.appendChild(a);
    a.click();
    window.URL.revokeObjectURL(url);
    document.body.removeChild(a);

    axiosInstance.post("/export_log", {
      export_type: "general_client_report",
      record_count: csvContent.length - 1,
      filters: {
        report: "GeneralClient",
        startDate: startDate ? startDate.format("YYYY-MM-DD") : null,
        endDate: endDate ? endDate.format("YYYY-MM-DD") : null,
      },
    });
  };

  const rangeLabel =
    startDate || endDate
      ? `${startDate ? startDate.format("MMM D, YYYY") : "Beginning"} – ${
          endDate ? endDate.format("MMM D, YYYY") : "Today"
        }`
      : "All time";

  const toolbar = (
    <Paper
      elevation={0}
      sx={{
        p: 2,
        mb: 3,
        borderRadius: "16px",
        border: CARD_BORDER,
        display: "flex",
        flexWrap: "wrap",
        gap: 2,
        alignItems: "center",
        justifyContent: "space-between",
      }}
    >
      <Box
        sx={{
          display: "flex",
          gap: 1.5,
          alignItems: "center",
          flexWrap: "wrap",
        }}
      >
        <LocalizationProvider dateAdapter={AdapterDayjs}>
          <DatePicker
            label="Start Date"
            value={startDate}
            onChange={(date) => setStartDate(date)}
            disableFuture={true}
            slotProps={{ textField: { size: "small" } }}
          />
          <DatePicker
            label="End Date"
            value={endDate}
            onChange={(date) => setEndDate(date)}
            shouldDisableDate={(date: Dayjs) =>
              startDate !== null && date < startDate
            }
            slotProps={{ textField: { size: "small" } }}
          />
        </LocalizationProvider>
        <Button
          variant="contained"
          onClick={() => fetchReport(startDate, endDate)}
          sx={{
            backgroundColor: PRIMARY,
            "&:hover": { backgroundColor: "#7A1B4D" },
          }}
        >
          Apply
        </Button>
        <Button
          variant="text"
          onClick={() => {
            setStartDate(null);
            setEndDate(null);
            fetchReport(null, null);
          }}
        >
          Reset
        </Button>
      </Box>
      <Box sx={{ display: "flex", gap: 1.5 }}>
        <Button
          variant="outlined"
          startIcon={<PrintIcon />}
          onClick={() => {
            toPDF();
            axiosInstance.post("/export_log", {
              export_type: "report_pdf",
              record_count: report ? (report.categories || []).length : 0,
              filters: {
                report: "GeneralClient",
                startDate: startDate ? startDate.format("YYYY-MM-DD") : null,
                endDate: endDate ? endDate.format("YYYY-MM-DD") : null,
              },
            });
          }}
        >
          Print
        </Button>
        <Button
          variant="outlined"
          startIcon={<FileDownloadIcon />}
          onClick={handleCsvDownload}
        >
          CSV
        </Button>
      </Box>
    </Paper>
  );

  const pageSx = {
    backgroundColor: "#f6f7f9",
    p: { xs: 2, md: 3 },
    minHeight: "100%",
  };

  if (loading && !report && !demographics && !operations) {
    return (
      <Box
        sx={{
          ...pageSx,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <CircularProgress sx={{ color: PRIMARY }} />
      </Box>
    );
  }

  const summary = report?.summary || {};
  const categories: any[] = report?.categories || [];
  const maxCategoryCount = categories.reduce(
    (max, row) => Math.max(max, row.count),
    0
  );

  const demoSummary = demographics?.summary || {};
  const ownership: any[] = demographics?.ownership || [];
  const byIndustry: any[] = demographics?.byIndustry || [];
  const byCity: any[] = demographics?.byCity || [];
  const byCounty: any[] = demographics?.byCounty || [];
  const byEmployeeBand: any[] = demographics?.byEmployeeBand || [];
  const byRevenueBand: any[] = demographics?.byRevenueBand || [];
  const maxCountyCount = byCounty.reduce(
    (max, row) => Math.max(max, row.count),
    0
  );

  const opsSummary = operations?.summary || {};
  const casesByMonth: any[] = operations?.casesByMonth || [];
  const byAdvisor: any[] = operations?.byAdvisor || [];
  const activityMix: any[] = operations?.activityMix || [];

  const pct = (fraction: any) => `${Math.round((fraction || 0) * 100)}%`;

  const hasGeneral = !!report && summary.total_businesses_served !== undefined;
  const hasDemographics =
    !!demographics && demoSummary.total_businesses_served !== undefined;
  const hasOperations = !!operations && opsSummary.total_cases !== undefined;

  return (
    <Box sx={pageSx}>
      {toolbar}
      <Box ref={targetRef}>
        <Box sx={{ mb: 3 }}>
          <Typography variant="h5" sx={{ fontWeight: 800, color: "#2c2c34" }}>
            General Client Report
          </Typography>
          <Typography variant="body2" sx={{ color: "#8a8a93", mt: 0.5 }}>
            {rangeLabel}
          </Typography>
        </Box>

        {!hasGeneral && !hasDemographics && !hasOperations ? (
          <SectionCard>
            <Typography sx={{ color: "#8a8a93" }}>
              No data available for the selected range.
            </Typography>
          </SectionCard>
        ) : (
          <>
            {hasGeneral ? (
              <>
                {/* Headline metrics */}
                <Box
                  sx={{
                    display: "flex",
                    flexWrap: "wrap",
                    gap: 2,
                    mb: 3,
                  }}
                >
                  <StatCard
                    label="Unique Businesses Served"
                    value={summary.total_businesses_served}
                    accent={PRIMARY}
                  />
                  <StatCard
                    label="Clients With Multiple Referrals"
                    value={summary.clients_multiple_referrals}
                    accent="#3F51B5"
                  />
                  <StatCard
                    label="Categories of Assistance"
                    value={summary.distinct_categories}
                    accent="#00897b"
                  />
                  <StatCard
                    label="Total Referrals"
                    value={summary.total_referrals}
                    accent="#fdb814"
                  />
                </Box>

                {/* Category detail table */}
                <SectionCard sx={{ mb: 4 }}>
                  <SectionTitle subtitle="General categories of assistance provided to clients">
                    Assistance Categories Breakdown
                  </SectionTitle>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell sx={{ fontWeight: 700 }}>Category</TableCell>
                        <TableCell align="right" sx={{ fontWeight: 700 }}>
                          Referrals
                        </TableCell>
                        <TableCell sx={{ fontWeight: 700, width: "40%" }}>
                          Share
                        </TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {categories.map((row, index) => (
                        <TableRow key={row.category}>
                          <TableCell>{row.category}</TableCell>
                          <TableCell align="right">{row.count}</TableCell>
                          <TableCell>
                            <Box
                              sx={{
                                display: "flex",
                                alignItems: "center",
                                gap: 1,
                              }}
                            >
                              <Box sx={{ flexGrow: 1 }}>
                                <LinearProgress
                                  variant="determinate"
                                  value={
                                    maxCategoryCount > 0
                                      ? (row.count / maxCategoryCount) * 100
                                      : 0
                                  }
                                  sx={{
                                    height: 7,
                                    borderRadius: 4,
                                    backgroundColor: "#eef0f2",
                                    "& .MuiLinearProgress-bar": {
                                      backgroundColor:
                                        CATEGORY_COLORS[
                                          index % CATEGORY_COLORS.length
                                        ],
                                      borderRadius: 4,
                                    },
                                  }}
                                />
                              </Box>
                              <Typography
                                variant="body2"
                                sx={{ minWidth: 40, textAlign: "right" }}
                              >
                                {Math.round((row.percentage || 0) * 100)}%
                              </Typography>
                            </Box>
                          </TableCell>
                        </TableRow>
                      ))}
                      {categories.length === 0 ? (
                        <TableRow>
                          <TableCell colSpan={3} sx={{ color: "#8a8a93" }}>
                            No referrals in the selected range.
                          </TableCell>
                        </TableRow>
                      ) : null}
                    </TableBody>
                  </Table>
                </SectionCard>
              </>
            ) : null}

            {hasDemographics ? (
              <>
                <GroupHeading subtitle="Who the businesses served are, and where they are">
                  Client Demographics
                </GroupHeading>

                {/* Demographic headline metrics */}
                <Box
                  sx={{
                    display: "flex",
                    flexWrap: "wrap",
                    gap: 2,
                    mb: 3,
                  }}
                >
                  <StatCard
                    label="Women-Owned"
                    value={pct(demoSummary.women_owned_pct)}
                    accent="#3F51B5"
                  />
                  <StatCard
                    label="Minority-Owned"
                    value={pct(demoSummary.minority_owned_pct)}
                    accent="#00897b"
                  />
                  <StatCard
                    label="Veteran-Owned"
                    value={pct(demoSummary.veteran_owned_pct)}
                    accent="#fdb814"
                  />
                </Box>

                {/* Ownership & equity */}
                <Box sx={{ mb: 3 }}>
                  <ChartCard
                    title="Ownership & Equity"
                    subtitle="Businesses served by owner demographic"
                    height={Math.max(280, ownership.length * 44)}
                  >
                    <BarChart
                      layout="vertical"
                      data={ownership}
                      margin={{ top: 8, right: 48, left: 16, bottom: 8 }}
                    >
                      <XAxis type="number" allowDecimals={false} hide />
                      <YAxis
                        type="category"
                        dataKey="label"
                        width={140}
                        tick={{ fontSize: 12 }}
                      />
                      <Tooltip
                        formatter={(value: any) => [value, "Businesses"]}
                      />
                      <Bar dataKey="count" radius={[0, 6, 6, 0]}>
                        {ownership.map((entry, index) => (
                          <Cell
                            key={`cell-${index}`}
                            fill={
                              CATEGORY_COLORS[index % CATEGORY_COLORS.length]
                            }
                          />
                        ))}
                        <LabelList dataKey="count" position="right" />
                      </Bar>
                    </BarChart>
                  </ChartCard>
                </Box>

                {/* Industries & Cities */}
                <Box
                  sx={{
                    display: "flex",
                    flexWrap: "wrap",
                    gap: 3,
                    mb: 3,
                  }}
                >
                  <ChartCard
                    title="Top Industries"
                    subtitle="Businesses served by industry"
                    height={Math.max(280, byIndustry.length * 34)}
                  >
                    <BarChart
                      layout="vertical"
                      data={byIndustry}
                      margin={{ top: 8, right: 48, left: 16, bottom: 8 }}
                    >
                      <XAxis type="number" allowDecimals={false} hide />
                      <YAxis
                        type="category"
                        dataKey="label"
                        width={180}
                        tick={{ fontSize: 12 }}
                      />
                      <Tooltip
                        formatter={(value: any) => [value, "Businesses"]}
                      />
                      <Bar dataKey="count" radius={[0, 6, 6, 0]}>
                        {byIndustry.map((entry, index) => (
                          <Cell
                            key={`ind-${index}`}
                            fill={
                              CATEGORY_COLORS[index % CATEGORY_COLORS.length]
                            }
                          />
                        ))}
                        <LabelList dataKey="count" position="right" />
                      </Bar>
                    </BarChart>
                  </ChartCard>

                  <ChartCard
                    title="Top Cities"
                    subtitle="Businesses served by city"
                    height={Math.max(280, byCity.length * 34)}
                  >
                    <BarChart
                      layout="vertical"
                      data={byCity}
                      margin={{ top: 8, right: 48, left: 16, bottom: 8 }}
                    >
                      <XAxis type="number" allowDecimals={false} hide />
                      <YAxis
                        type="category"
                        dataKey="label"
                        width={180}
                        tick={{ fontSize: 12 }}
                      />
                      <Tooltip
                        formatter={(value: any) => [value, "Businesses"]}
                      />
                      <Bar dataKey="count" radius={[0, 6, 6, 0]}>
                        {byCity.map((entry, index) => (
                          <Cell
                            key={`city-${index}`}
                            fill={
                              CATEGORY_COLORS[index % CATEGORY_COLORS.length]
                            }
                          />
                        ))}
                        <LabelList dataKey="count" position="right" />
                      </Bar>
                    </BarChart>
                  </ChartCard>
                </Box>

                {/* Business size & revenue */}
                <Box
                  sx={{
                    display: "flex",
                    flexWrap: "wrap",
                    gap: 3,
                    mb: 3,
                  }}
                >
                  <ChartCard
                    title="Business Size (Employees)"
                    subtitle="Businesses served by employee count"
                    height={320}
                  >
                    <BarChart
                      data={byEmployeeBand}
                      margin={{ top: 8, right: 16, left: 0, bottom: 8 }}
                    >
                      <XAxis
                        dataKey="label"
                        tick={{ fontSize: 12 }}
                        interval={0}
                      />
                      <YAxis allowDecimals={false} tick={{ fontSize: 12 }} />
                      <Tooltip
                        formatter={(value: any) => [value, "Businesses"]}
                      />
                      <Bar dataKey="count" radius={[6, 6, 0, 0]}>
                        {byEmployeeBand.map((entry, index) => (
                          <Cell
                            key={`emp-${index}`}
                            fill={
                              CATEGORY_COLORS[index % CATEGORY_COLORS.length]
                            }
                          />
                        ))}
                        <LabelList dataKey="count" position="top" />
                      </Bar>
                    </BarChart>
                  </ChartCard>

                  <ChartCard
                    title="Annual Revenue"
                    subtitle="Businesses served by revenue band"
                    height={320}
                  >
                    <BarChart
                      data={byRevenueBand}
                      margin={{ top: 8, right: 16, left: 0, bottom: 8 }}
                    >
                      <XAxis
                        dataKey="label"
                        tick={{ fontSize: 12 }}
                        interval={0}
                      />
                      <YAxis allowDecimals={false} tick={{ fontSize: 12 }} />
                      <Tooltip
                        formatter={(value: any) => [value, "Businesses"]}
                      />
                      <Bar dataKey="count" radius={[6, 6, 0, 0]}>
                        {byRevenueBand.map((entry, index) => (
                          <Cell
                            key={`rev-${index}`}
                            fill={
                              CATEGORY_COLORS[index % CATEGORY_COLORS.length]
                            }
                          />
                        ))}
                        <LabelList dataKey="count" position="top" />
                      </Bar>
                    </BarChart>
                  </ChartCard>
                </Box>

                {/* Geographic reach — county table */}
                <SectionCard sx={{ mb: 4 }}>
                  <SectionTitle subtitle="Businesses served by county">
                    Geographic Reach — County
                  </SectionTitle>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell sx={{ fontWeight: 700 }}>County</TableCell>
                        <TableCell align="right" sx={{ fontWeight: 700 }}>
                          Businesses
                        </TableCell>
                        <TableCell sx={{ fontWeight: 700, width: "40%" }}>
                          Share
                        </TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {byCounty.map((row, index) => (
                        <TableRow key={row.label}>
                          <TableCell>{row.label}</TableCell>
                          <TableCell align="right">{row.count}</TableCell>
                          <TableCell>
                            <Box
                              sx={{
                                display: "flex",
                                alignItems: "center",
                                gap: 1,
                              }}
                            >
                              <Box sx={{ flexGrow: 1 }}>
                                <LinearProgress
                                  variant="determinate"
                                  value={
                                    maxCountyCount > 0
                                      ? (row.count / maxCountyCount) * 100
                                      : 0
                                  }
                                  sx={{
                                    height: 7,
                                    borderRadius: 4,
                                    backgroundColor: "#eef0f2",
                                    "& .MuiLinearProgress-bar": {
                                      backgroundColor:
                                        CATEGORY_COLORS[
                                          index % CATEGORY_COLORS.length
                                        ],
                                      borderRadius: 4,
                                    },
                                  }}
                                />
                              </Box>
                            </Box>
                          </TableCell>
                        </TableRow>
                      ))}
                      {byCounty.length === 0 ? (
                        <TableRow>
                          <TableCell colSpan={3} sx={{ color: "#8a8a93" }}>
                            No businesses in the selected range.
                          </TableCell>
                        </TableRow>
                      ) : null}
                    </TableBody>
                  </Table>
                </SectionCard>
              </>
            ) : null}

            {hasOperations ? (
              <>
                <GroupHeading subtitle="Case volume, advisor workload and engagement activity">
                  Engagement Operations
                </GroupHeading>

                {/* Operations headline metrics */}
                <Box
                  sx={{
                    display: "flex",
                    flexWrap: "wrap",
                    gap: 2,
                    mb: 3,
                  }}
                >
                  <StatCard
                    label="Total Cases"
                    value={opsSummary.total_cases}
                    accent={PRIMARY}
                  />
                  <StatCard
                    label="Open"
                    value={opsSummary.open_cases}
                    accent="#fdb814"
                  />
                  <StatCard
                    label="Completed"
                    value={opsSummary.completed_cases}
                    accent="#00897b"
                  />
                  <StatCard
                    label="Active Advisors"
                    value={opsSummary.active_advisors}
                    accent="#3F51B5"
                  />
                  <StatCard
                    label="Avg Case Cycle"
                    value={
                      opsSummary.avg_case_duration_days == null
                        ? "—"
                        : Math.round(opsSummary.avg_case_duration_days) + "d"
                    }
                    accent="#7e57c2"
                  />
                </Box>

                {/* Case flow over time */}
                <Box sx={{ mb: 3 }}>
                  <ChartCard
                    title="Case Flow Over Time"
                    subtitle="Cases created vs. closed by month"
                    height={320}
                  >
                    <LineChart
                      data={casesByMonth}
                      margin={{ top: 8, right: 24, left: 0, bottom: 8 }}
                    >
                      <CartesianGrid strokeDasharray="3 3" stroke="#eef0f2" />
                      <XAxis
                        dataKey="month"
                        tick={{ fontSize: 12 }}
                        interval="preserveStartEnd"
                      />
                      <YAxis allowDecimals={false} tick={{ fontSize: 12 }} />
                      <Tooltip />
                      <Legend />
                      <Line
                        type="monotone"
                        dataKey="created"
                        name="Created"
                        stroke={PRIMARY}
                        strokeWidth={2}
                        dot={false}
                      />
                      <Line
                        type="monotone"
                        dataKey="closed"
                        name="Closed"
                        stroke="#00897b"
                        strokeWidth={2}
                        dot={false}
                      />
                    </LineChart>
                  </ChartCard>
                </Box>

                {/* Advisor workload + activity mix */}
                <Box
                  sx={{
                    display: "flex",
                    flexWrap: "wrap",
                    gap: 3,
                    mb: 3,
                  }}
                >
                  <ChartCard
                    title="Advisor Workload"
                    subtitle="Open vs. completed cases per advisor (top 15)"
                    height={Math.max(300, byAdvisor.length * 36)}
                  >
                    <BarChart
                      layout="vertical"
                      data={byAdvisor}
                      margin={{ top: 8, right: 24, left: 16, bottom: 8 }}
                    >
                      <CartesianGrid strokeDasharray="3 3" stroke="#eef0f2" />
                      <XAxis type="number" allowDecimals={false} />
                      <YAxis
                        type="category"
                        dataKey="advisor_name"
                        width={150}
                        tick={{ fontSize: 12 }}
                      />
                      <Tooltip />
                      <Legend />
                      <Bar
                        dataKey="open"
                        name="Open"
                        stackId="a"
                        fill="#fdb814"
                      />
                      <Bar
                        dataKey="completed"
                        name="Completed"
                        stackId="a"
                        fill="#00897b"
                      />
                    </BarChart>
                  </ChartCard>

                  <ChartCard
                    title="Activity Mix"
                    subtitle="Advisor–client engagement activity (excludes mass event invitations)"
                    height={Math.max(300, activityMix.length * 34)}
                  >
                    <BarChart
                      layout="vertical"
                      data={activityMix}
                      margin={{ top: 8, right: 48, left: 16, bottom: 8 }}
                    >
                      <XAxis type="number" allowDecimals={false} hide />
                      <YAxis
                        type="category"
                        dataKey="label"
                        width={180}
                        tick={{ fontSize: 12 }}
                      />
                      <Tooltip
                        formatter={(value: any) => [value, "Activities"]}
                      />
                      <Bar dataKey="count" radius={[0, 6, 6, 0]}>
                        {activityMix.map((entry, index) => (
                          <Cell
                            key={`cell-${index}`}
                            fill={
                              CATEGORY_COLORS[index % CATEGORY_COLORS.length]
                            }
                          />
                        ))}
                        <LabelList dataKey="count" position="right" />
                      </Bar>
                    </BarChart>
                  </ChartCard>
                </Box>
              </>
            ) : null}
          </>
        )}
      </Box>
    </Box>
  );
};

export default GeneralClientReport;
